/* « Faire pointer vers… » — the ONE control of the support ↔ campaign relation (Scope v5 §7, batch 20).
   A support's code never changes; what it opens changes only here. Two values: « la campagne principale de
   l'établissement » (I-1: its principale, else the first live — no action needed) or « cette campagne » (fixed — a
   poster for an event while the counter keeps the loyalty campaign). The change says who loses the support.
   Used by Supports (list + detail), the campaign's Supports tab and the builder's step 4 — the same rows,
   the same verb. « Tester ce QR » runs the real journey in test mode: nothing counted (MA-8, FQ-I16: a signed test link).
   MA-27 (as ruled, orchestrator/residue-b-vet.md): the select shows only when it offers a choice — two campaigns or
   more it could fix the support on (en ligne or programmée, `candidatesFor`) at the support's establishment, or a
   support already fixed on one (it keeps its way back). Otherwise nothing: every row and the support's page already
   say what it opens (servedSentence). « Tester ce QR » stays either way.
   A support fixed on a campaign that has since left the candidates (paused, ended, a brouillon) keeps its select, and
   the select carries that campaign as its own option, chosen, in the sentence's words (residue-b-vet « Re-vet · v2 »):
   it shows what the sentence says, and choosing anything else is a change. */
import { useRef, useState } from 'react'
import { qrSvg } from '@brand/qr/qr.js'
import { Button, Icon, Modal, Select, useToast } from '@/ui'
import { ApiError, getTestLink, type TestLink } from '@/lib/api'
import { pointerChange, servedBy, type CampaignLite, type SupportRef } from '@/lib/campaign'
import { supportRefusal, useSupportWrites } from '@/lib/campaign-api'

const candidatesFor = (support: SupportRef, list: CampaignLite[]) =>
  list.filter(c => (c.status === 'active' || c.status === 'scheduled') && c.locations.includes(support.locationId))
/** MA-27 · is there anything to choose? Two of the select's own campaigns here, or a support already fixed on one. */
export const canRepoint = (support: SupportRef, list: CampaignLite[]) => candidatesFor(support, list).length >= 2 || !!support.pinned
/** The option of a pin the candidates no longer hold, in servedSentence's words: « … · en pause », « · terminée »,
    « · pas encore publiée » (« · programmée » when paused before its start, as the sentence says). */
function pinnedOption(support: SupportRef, list: CampaignLite[]): string {
  const p = list.find(c => c.id === support.pinned)
  if (!p) return 'Une campagne qui n’existe plus'
  const { reason } = servedBy(support, list)
  const state = reason === 'fixed_paused' ? ' · en pause'
    : reason === 'fixed_scheduled' ? (p.status === 'draft' ? ' · pas encore publiée' : ' · programmée')
    : p.status === 'ended' || p.status === 'archived' ? ' · terminée' : ''
  return `Cette campagne : « ${p.name} »${state}`
}

export function PointerSelect({ support, list, placeName, onChanged, highlight }: { support: SupportRef; list: CampaignLite[]; placeName: string; onChanged: () => void; highlight?: string }) {
  const toast = useToast()
  const candidates = candidatesFor(support, list)
  const cur = servedBy(support, list).current
  const lost = !!support.pinned && !candidates.some(c => c.id === support.pinned)
  /* item 3 of the b-next-row ruling: with an API the change goes to it (POST …/pointer) and the list takes the support
     it answers; a refusal is a toast in the app's words and the select keeps what the support does */
  const { point } = useSupportWrites()
  const [busy, setBusy] = useState(false)
  const inFlight = useRef(false)
  const change = async (v: string) => {
    const target = v || null
    if (inFlight.current || (support.pinned ?? null) === target) return
    let ch = pointerChange(support, target, list, placeName)
    inFlight.current = true; setBusy(true)
    try {
      /* row 3 vet: with an API the toast says what the server answers (`from`, `to`), the demo what its resolver reads */
      const moved = await point(support, target)
      if (moved) ch = {
        from: moved.from && moved.from.campaignId !== moved.to?.campaignId ? moved.from.name : null,
        to: target ? `« ${moved.to?.name ?? list.find(c => c.id === target)?.name ?? '…'} » (fixe)` : `la campagne principale de ${placeName}${moved.to ? ` — aujourd’hui « ${moved.to.name} »` : ' — aucune aujourd’hui'}`,
      }
    } catch (e) {
      toast({ message: supportRefusal(e) })
      return
    } finally { inFlight.current = false; setBusy(false) }
    toast({ message: `« ${support.name} » pointe vers ${ch.to}.${ch.from ? ` « ${ch.from} » perd ce support.` : ''} Rien à réimprimer.` })
    onChanged()
  }
  if (!canRepoint(support, list)) return null
  return (
    <Select aria-label={`Faire pointer « ${support.name} » vers`} value={support.pinned ?? ''} disabled={busy} onChange={e => { void change(e.target.value) }} style={{ maxWidth: 340 }}>
      <option value="">La campagne principale de {placeName}{!support.pinned && cur ? ` — aujourd’hui « ${cur.name} »` : !support.pinned ? ' — aucune aujourd’hui' : ''}</option>
      {lost && <option value={support.pinned!}>{pinnedOption(support, list)}</option>}
      {candidates.map(c => <option key={c.id} value={c.id}>Cette campagne : « {c.name} »{c.id === highlight ? ' (celle-ci)' : ''}{c.status === 'scheduled' ? ' · programmée' : ''}</option>)}
    </Select>
  )
}

/** « Tester ce QR » — the journey the support opens today, in test mode (Scope v5 · every row). MA-8 / FQ-I16: the QR of
    a test link the API signs for this support, two hours. A fresh one is asked each time it is shown, and kept nowhere:
    closing forgets it. No tick is set here — the test read sees the scan and the play when they happen. */
export function TesterQR({ support, list, size = 'sm' }: { support: SupportRef; list: CampaignLite[]; size?: 'sm' | 'md' }) {
  const cur = servedBy(support, list).current
  const [open, setOpen] = useState(false)
  const [link, setLink] = useState<TestLink | null>(null)
  const [err, setErr] = useState<string | null>(null)
  const ask = useRef(0)
  const fetchLink = () => {
    const n = ++ask.current
    setLink(null); setErr(null)
    getTestLink(support.id).then(l => { if (n === ask.current) setLink(l) },
      (e: unknown) => { if (n === ask.current) setErr(e instanceof ApiError ? e.message : 'Le QR de test ne s’affiche pas.') })
  }
  const close = () => { ask.current++; setOpen(false); setLink(null); setErr(null) }
  /* @brand/qr throws on an empty url: then no QR is drawn, and the screen says so */
  let svg = ''
  if (link) { try { svg = qrSvg(link.url, { label: `QR de test de « ${support.name} »` }) } catch { svg = '' } }
  const failed = err ?? (link && !svg ? 'Le QR de test ne s’affiche pas.' : null)
  return <>
    <Button size={size} variant="quiet" disabled={!cur} title={cur ? 'Le vrai parcours, en mode test — rien ne compte' : 'Ce support n’ouvre rien aujourd’hui'} onClick={() => { setOpen(true); fetchLink() }}><Icon name="phone" /> Tester ce QR</Button>
    <Modal open={open} title="Tester ce QR" onClose={close} actions={<Button onClick={close}>Fermer</Button>}>
      <div style={{ display: 'grid', gap: 12, justifyItems: 'center', textAlign: 'center' }} data-testid="tester-qr">
        <p className="t-body" style={{ margin: 0 }}>Visez ce QR avec l’appareil photo de votre téléphone&nbsp;: le vrai parcours de «&nbsp;{support.name}&nbsp;», en mode test. Rien ne compte dans vos résultats.</p>
        {failed
          ? <><p className="f-help" style={{ margin: 0 }} data-testid="tester-qr-erreur">{failed}</p><Button size="sm" onClick={fetchLink}>Réessayer</Button></>
          : !link ? <p className="f-help" style={{ margin: 0 }}>Chargement…</p>
          : <>
            <div data-testid="tester-qr-svg" style={{ width: 220, maxWidth: '100%', lineHeight: 0 }} dangerouslySetInnerHTML={{ __html: svg }} />
            <span className="f-help" style={{ margin: 0 }}>Valable deux heures. Rouvrez «&nbsp;Tester ce QR&nbsp;» pour un nouveau.</span>
            <a href={link.url} target="_blank" rel="noopener" data-testid="tester-qr-ici">Pas de téléphone&nbsp;? Jouer ici</a>
          </>}
      </div>
    </Modal>
  </>
}
